import { createTheme } from "@mui/material/styles";
import type { PaletteColorOptions } from "@mui/material/styles";
import type { CSSProperties } from "react";
import { COLORS } from "./colors";

declare module "@mui/material/styles" {
  interface Palette {
    brandBackground: Palette["primary"];
    greys: {
      dark: string;
      medium: string;
      light: string;
    };
  }
  interface PaletteOptions {
    brandBackground?: PaletteColorOptions;
    greys?: {
      dark: string;
      medium: string;
      light: string;
    };
  }
  interface TypographyVariants {
    bodyBold: CSSProperties;
    bodySmall: CSSProperties;
    bodySmallLight: CSSProperties;
  }
  interface TypographyVariantsOptions {
    bodyBold?: CSSProperties;
    bodySmall?: CSSProperties;
    bodySmallLight?: CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    bodyBold: true;
    bodySmall: true;
    bodySmallLight: true;
  }
}

export const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: COLORS.primary,
      contrastText: COLORS.white,
    },
    secondary: {
      main: COLORS.secondary,
      contrastText: COLORS.white,
    },
    background: {
      default: COLORS.lightGrey,
      paper: COLORS.white,
    },
    text: {
      primary: COLORS.black,
      secondary: COLORS.darkGrey,
    },
    brandBackground: {
      main: COLORS.brandBackground,
      contrastText: COLORS.white,
    },
    greys: {
      dark: COLORS.darkGrey,
      medium: COLORS.mediumGrey,
      light: COLORS.lightGrey,
    },
  },
  typography: {
    fontFamily:
      '"TT Norms Pro", "TTNormsPro", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"TT Norms Pro"',
      fontSize: "48px",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "120%",
    },
    h2: {
      fontFamily: '"TT Norms Pro"',
      fontSize: "32px",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "120%",
    },
    h3: {
      fontFamily: '"TT Norms Pro"',
      fontSize: "24px",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "120%",
    },
    body1: {
      fontFamily: '"TT Norms Pro"',
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "120%",
    },
    bodyBold: {
      fontFamily: '"TT Norms Pro"',
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "120%",
    },
    bodySmall: {
      fontFamily: '"TT Norms Pro"',
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "132%",
    },
    bodySmallLight: {
      fontFamily: '"TT Norms Pro"',
      fontSize: "12px",
      fontStyle: "normal",
      fontWeight: 700,
      lineHeight: "100%",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        :root {
          --color-primary: ${COLORS.primary};
          --color-secondary: ${COLORS.secondary};
          --color-brand-bg: ${COLORS.brandBackground};
          --color-grey-dark: ${COLORS.darkGrey};
          --color-grey-medium: ${COLORS.mediumGrey};
          --color-grey-light: ${COLORS.lightGrey};
          --color-white: ${COLORS.white};
          --color-black: ${COLORS.black};
        }
      `,
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          padding: 0,
          margin: 0,
          display: "inline-block",
          verticalAlign: "middle",
          boxSizing: "content-box",
          width: "1em",
          height: "1em",
          overflow: "visible", // helps when viewBox has padding
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          padding: 0, // removes padding
          margin: 0, // optional: also resets margin if needed
        },
      },
    },
  },
});

export type AppTheme = typeof theme;
